import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Live MDR + Settlement fee preview for any place a partner picks a top-up
 * gateway/channel/amount before opening the PG. Mirrors `/topup-fees/preview`
 * — debounced 350 ms, reactive to amount/gateway/channel changes.
 *
 * Used in:
 *   - BBPS V2 customer wallet top-up dialog (inline copy still there for parity)
 *   - Customer Wallet page top-up dialog
 *   - Partner Payment Link creation form
 *
 * Props:
 *   amount  : number (₹)
 *   gateway : "razorpay" | "cashfree" | "manarecharge" | "runpaisa"
 *   channel : string | null     (e.g. "PAY0", "PAY2", "PAY3")
 *   testidPrefix : string       (so multiple instances on a page get unique data-testids)
 *   compact : bool              (smaller padding for inline use)
 */
export default function TopupFeePreview({ amount, gateway, channel, testidPrefix = "fee-preview", compact = false }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || !gateway) {
      setPreview(null); setError(""); return;
    }
    setLoading(true);
    setError("");
    let cancel = false;
    const tid = setTimeout(async () => {
      try {
        const { data } = await api.post("/topup-fees/preview", {
          gateway, amount: amt, channel: channel || null,
        });
        if (!cancel) setPreview(data);
      } catch (err) {
        if (!cancel) {
          setPreview(null);
          setError(formatApiError(err.response?.data?.detail) || "Fee preview unavailable");
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 350);
    return () => { cancel = true; clearTimeout(tid); };
  }, [amount, gateway, channel]);

  const amt = parseFloat(amount);
  if (!amt || amt <= 0) return null;

  return (
    <div
      className={`rounded-md border border-border bg-muted/20 text-[11px] space-y-1 ${compact ? "p-2" : "p-2.5"}`}
      data-testid={`${testidPrefix}-root`}
    >
      <div className="font-semibold flex items-center justify-between">
        <span>Fee preview</span>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
      {error ? (
        <div className="text-destructive" data-testid={`${testidPrefix}-error`}>{error}</div>
      ) : preview ? (
        <div className="font-mono">
          <div className="flex justify-between"><span>Top-up amount</span><span>₹{amt.toFixed(2)}</span></div>
          <div className="flex justify-between text-muted-foreground">
            <span>+ PG MDR fee</span>
            <span data-testid={`${testidPrefix}-mdr`}>₹{Number(preview.mdr_fee || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold border-t border-border mt-1 pt-1">
            <span>Customer pays at PG</span>
            <span data-testid={`${testidPrefix}-gross`}>₹{Number(preview.gross_amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground mt-1">
            <span>− Settlement fee</span>
            <span data-testid={`${testidPrefix}-settle`}>₹{Number(preview.settle_fee || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold text-success border-t border-border mt-1 pt-1">
            <span>Credited to wallet</span>
            <span data-testid={`${testidPrefix}-credited`}>₹{Number(preview.credited_amount || 0).toFixed(2)}</span>
          </div>
          <div className="text-[9px] text-muted-foreground mt-1 italic">
            Fee source: {preview.fee_source || "master"}
          </div>
        </div>
      ) : (
        <div className="text-muted-foreground">Enter amount + pick gateway to see fees…</div>
      )}
    </div>
  );
}
